import { Button, Flex } from 'antd';
import classNames from 'classnames';
import type { FC } from 'react';

import type { AnalyticsTopic } from '@shared/api';
import { Text } from '@shared/ui/antd';
import { FetchingErrorState } from '@shared/ui/fetching-error-state/fetching-error-state';
import { LoadingState } from '@shared/ui/loading-state/loading-state';

import type { TopicOption } from '../lib/dialog-summary-utils';
import { sumTopicQuestions } from '../lib/dialog-summary-utils';
import { DialogSummaryFilters } from './dialog-summary-filters';
import styles from '../dialog-summary.module.scss';

type DialogSummaryTopicsTabProps = {
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  topicsPlaceholder: string;
  noDataLabel: string;
  selectedTopics: string[];
  topicOptions: TopicOption[];
  filteredTopics: AnalyticsTopic[];
  visibleOpenTopics: Set<string>;
  onTopicChange: (value: unknown) => void;
  onToggleTopic: (topic: string) => void;
};

export const DialogSummaryTopicsTab: FC<DialogSummaryTopicsTabProps> = ({
  isLoading,
  isError,
  refetch,
  topicsPlaceholder,
  noDataLabel,
  selectedTopics,
  topicOptions,
  filteredTopics,
  visibleOpenTopics,
  onTopicChange,
  onToggleTopic,
}) => (
  <>
    <DialogSummaryFilters
      placeholder={topicsPlaceholder}
      selectedTopics={selectedTopics}
      topicOptions={topicOptions}
      onTopicChange={onTopicChange}
    />

    {isLoading && <LoadingState />}
    {!isLoading && isError && <FetchingErrorState refetch={refetch} />}

    {!isLoading && !isError && (
      <>
        {!filteredTopics.length && (
          <Text variant="sm" style={{ color: 'var(--color-text-tertiary)' }}>
            {noDataLabel}
          </Text>
        )}

        {!!filteredTopics.length && (
          <Flex vertical className={styles.content}>
            <div className={styles.listScroll}>
              <Flex vertical className={styles.topicList}>
                {filteredTopics.map((topic) => {
                  const isOpen = visibleOpenTopics.has(topic.topic);
                  const topicCount = sumTopicQuestions(topic);
                  return (
                    <Flex vertical className={styles.topicItem} key={topic.topic}>
                      <Button
                        type="text"
                        className={styles.topicHeader}
                        onClick={() => onToggleTopic(topic.topic)}
                      >
                        <Flex align="center" justify="space-between" className={styles.topicMeta}>
                          <Text variant="md" fontWeight={400} hasTooltip maxLength={60}>
                            {topic.topic}
                          </Text>
                          <Flex align="center" gap={8}>
                            <Text variant="sm" fontWeight={400} className={styles.topicCount}>
                              {topicCount}
                            </Text>
                            <span
                              className={classNames(styles.topicChevron, {
                                [styles.topicChevronOpen]: isOpen,
                              })}
                            />
                          </Flex>
                        </Flex>
                      </Button>

                      {isOpen && (
                        <Flex vertical className={classNames(styles.questionList, styles.nested)}>
                          {topic.questions.map((question) => (
                            <Flex
                              align="center"
                              justify="space-between"
                              className={styles.questionRow}
                              key={`${topic.topic}-${question.question}`}
                            >
                              <Text variant="md" className={styles.questionText} hasTooltip maxLength={60}>
                                {question.question}
                              </Text>
                              <Text variant="sm" fontWeight={400} className={styles.questionCount}>
                                {question.count}
                              </Text>
                            </Flex>
                          ))}
                        </Flex>
                      )}
                    </Flex>
                  );
                })}
              </Flex>
            </div>
          </Flex>
        )}
      </>
    )}
  </>
);
